import { useState, useEffect, useMemo } from "react"
import { useNavigate, useLocation, Link } from "react-router-dom"
import { FaTimes, FaFileInvoice } from "react-icons/fa";
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify'
import Server from "../../Shared/Server"
import Spinner from '../../Shared/Spinner'
import BackButton from '../../Shared/BackButton'
import DataGrid from '../../Shared/DataGrid';

const DailyCollectionDetail = () => {
    const [collection, setCollection] = useState([]);
    const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
    const [title, setTitle] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [show, setShow] = useState(false);
    const [publicID, setPublicID] = useState();
    const navigate = useNavigate();
    const location = useLocation();


    useEffect(() => {
        let isMounted = true
        const fetchData = async (payload) => {
            setIsLoading(true)
            try {
                const response = await Server.get(`/reports/${payload.by}-collection/?fdate=${payload.fdate}&tdate=${payload.tdate}&public_id=${payload.public_id}`)
                if(isMounted){
                    setCollection(response?.data)
                }
                setIsLoading(false)

                const output = response?.data.reduce((data, row) => {
                    data.due += parseFloat(row.due);
                    data.paid += parseFloat(row.paid);
                    data.discount += parseFloat(row.discount);
                    data.balance += parseFloat(row.balance);
                    return data;
                }, {due: 0.00, paid: 0.00, discount: 0.00, balance: 0.00});

                setPinnedBottomRowData([
                    {
                        due: parseFloat(output.due).toFixed(2),
                        paid: parseFloat(output.paid).toFixed(2),
                        discount: parseFloat(output.discount).toFixed(2),
                        balance: parseFloat(output.balance).toFixed(2),
                    }
                ]);
            } catch (error) {
                setIsLoading(false)
                if(error?.response?.status === 401){
                    localStorage.removeItem('user')
                    navigate('/login')
                }else if (error?.response?.status === 403){
                    toast.error(error?.response?.data?.detail || 'Forbidden');
                    navigate('/');
                }else {
                    toast.error(error?.response?.data?.detail || 'Failed to load collection details')
                }
            }
        }

        if (location.state){
            const {data} = location.state;

            fetchData({
                fdate: data.fdate,
                tdate: data.tdate,
                public_id: data.public_id,
                by: data.by
            })
            setTitle(`${data.name}, Collection Report From ${data.fdate} To ${data.tdate}`)

        }

        return () => (isMounted = false);
    }, [])

    const handleClose = () => {
        if (isDeleting) return;
        setShow(false)
    }
    const handleShow = (public_id) => {
        setShow(true)
        setPublicID(public_id)
    };

    const handleDelete = async (public_id) => {
        setIsDeleting(true)
        try {
            const response = await Server.delete(`/payments/${public_id}`)
            if (response.status === 200){
                toast.success(response.data.detail)
                setCollection((prev) => prev.filter((coll) => coll.public_id !== public_id))
            }else{
                toast.error(response?.data?.detail || 'Failed to cancel payment')
            }
        } catch (error) {
            if(error?.response?.status === 401){
                navigate('/login')
            }else if (error?.response?.status === 403){
                toast.error(error?.response?.data?.detail || 'Forbidden');
                navigate('/');
            }else{
                toast.error(error?.response?.data?.detail || 'Failed to cancel payment')
            }
        } finally {
            setIsDeleting(false)
            setShow(false)
        }
    }

    const columnDefs = useMemo(() => [
        {
            headerName: "#",
            filter: false,
            maxWidth: 90,
            valueFormatter: function (params) {
                if (isNaN(params.node.id)) {
                    return "";
                }
                return `${Number(params.node.rowIndex) + 1}`;
            },
        },
        {
            headerName: "Date",
            field: "date",
            filter: 'agDateColumnFilter',
            filterParams: {
                comparator: (filterDate, cellValue) => {
                    const cellDate = new Date(cellValue);
                    cellDate.setHours(0, 0, 0, 0);
                    filterDate.setHours(0, 0, 0, 0);

                    if (cellDate < filterDate) return -1;
                    if (cellDate > filterDate) return 1;
                    return 0;
                },
            },
        },
        { headerName: "PID", field: "previous_id" },
        { headerName: "ID", field: "location_id" },
        { headerName: "Name", field: "full_name" },
        { headerName: "Remark", field: "remark" },
        { headerName: "Contact", field: "contact_number" },
        {
            headerName: "Due",
            field: "due",
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB,
        },
        {
            headerName: "Paid",
            field: "paid",
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB,
        },
        {
            headerName: "Discount",
            field: "discount",
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB,
        },
        {
            headerName: "Balance",
            field: "balance",
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB,
        },
        { headerName: "Book", field: "book" },
        {
            headerName: "Page",
            field: "page",
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB,
        },
        { headerName: "Rate", field: "rate_name" },
        { headerName: "Village", field: "village_name" },
        { headerName: "Zone", field: "zone_name" },
        { headerName: "Station", field: "station_name" },
        { headerName: "Line", field: "line_name" },
        {
            headerName: "Actions",
            pinned: 'left',
            minWidth: 120,
            sortable: false,
            filter: false,
            cellRenderer: (e) => {
                if (!e.data) return null;
                if (e.node.rowPinned) return '';
                const payload = {
                    year: e.data.year,
                    month: e.data.month,
                    contact_number: e.data.contact_number,
                };
                return (
                    <div className="d-flex gap-1">
                        <button
                            className="btn btn-outline-danger"
                            title="Cancel payment"
                            onClick={() => handleShow(e.data.public_id)}
                        >
                            <FaTimes/>
                        </button>
                        <Link
                            className="btn btn-outline-success"
                            title="View receipt"
                            to="/receipt"
                            state={{ payload: payload }}
                        >
                            <FaFileInvoice/>
                        </Link>
                    </div>
                );
            },
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], []);

    const onFilterChanged = (params) => {
        const filteredRows = [];
        params.api.forEachNodeAfterFilter(node => filteredRows.push(node.data));

        const output = filteredRows.reduce((data, row) => {
            data.due += Number(row.due) || 0;
            data.paid += Number(row.paid) || 0;
            data.discount += Number(row.discount) || 0;
            data.balance += Number(row.balance) || 0;
            return data;
        }, { due: 0.00, paid: 0.00, discount: 0.00, balance: 0.00 });

        setPinnedBottomRowData([
            {
                due: output.due.toFixed(2),
                paid: output.paid.toFixed(2),
                discount: output.discount.toFixed(2),
                balance: output.balance.toFixed(2),
            }
        ]);
    };

    if(isLoading){
        return <Spinner/>
    }

    return (
        <>
            <DataGrid
                title={title}
                rowData={collection}
                columnDefs={columnDefs}
                exportFileName={title}
                pinnedBottomRowData={pinnedBottomRowData}
                leftActions={
                    <BackButton
                        to="/daily-collection-summary"
                        state={{ fdate: location.state?.data?.fdate, tdate: location.state?.data?.tdate, by: location.state?.data?.by }}
                        label="Back to Summary"
                    />
                }
                onFilterChanged={onFilterChanged}
            />

            <Modal show={show} onHide={handleClose} backdrop={isDeleting ? 'static' : true}>
                <Modal.Header closeButton={!isDeleting}>
                    <Modal.Title>Delete Alert</Modal.Title>
                </Modal.Header>
                <Modal.Body>Are you sure?</Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleClose} disabled={isDeleting}>
                        No
                    </Button>
                    <Button variant="danger" onClick={() => handleDelete(publicID)} disabled={isDeleting}>
                        {isDeleting ? 'Deleting\u2026' : 'Yes'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default DailyCollectionDetail
